interface RevealNode {
  shell?: Element;
  text?: string;
  characters?: string[];
  children: RevealNode[];
  length: number;
  element?: HTMLElement;
  painted: number;
  faded: boolean;
}

/** Receives sanitized markup only. Keep displayed DOM alive as the reveal advances. */
export class ResponseDomRenderer {
  private nodes: RevealNode[] = [];
  private length = 0;
  private host?: HTMLElement;

  setDocument(fragment: DocumentFragment): void {
    this.nodes = this.readChildren(fragment, this.nodes);
    this.length = this.nodes.reduce((total, node) => total + node.length, 0);
  }

  render(host: HTMLElement, characters: number, fadeCharacters = 0): void {
    if (this.host !== host) {
      this.host = host;
      this.clearElements(this.nodes);
    }
    this.paintChildren(
      host,
      this.nodes,
      characters,
      Math.floor(characters - fadeCharacters),
      fadeCharacters,
      0,
      characters >= this.length
    );
  }

  private readChildren(parent: Node, previous: RevealNode[]): RevealNode[] {
    return Array.from(parent.childNodes)
      .filter(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE ||
          node.nodeType === Node.TEXT_NODE
      )
      .map((source, index) => {
        const old = previous[index];
        if (source.nodeType === Node.TEXT_NODE) {
          const text = source.textContent ?? '';
          if (old?.text === text) return old;
          const characters = Array.from(text);
          return {
            text,
            characters,
            length: characters.length,
            children: [],
            painted: -1,
            faded: false,
            element: old?.text !== undefined ? old.element : undefined,
          };
        }
        const element = source as Element;
        const same =
          old?.shell?.nodeName === element.nodeName &&
          old.shell.attributes.length === element.attributes.length &&
          Array.from(element.attributes).every(
            (attribute) =>
              old.shell!.getAttribute(attribute.name) === attribute.value
          );
        const children = this.readChildren(source, same ? old.children : []);
        if (
          same &&
          children.length === old.children.length &&
          children.every((child, index) => child === old.children[index])
        )
          return old;
        return {
          shell: same ? old.shell : (element.cloneNode(false) as Element),
          children,
          length: children.reduce((total, child) => total + child.length, 0),
          painted: -1,
          faded: false,
          element: same ? old.element : undefined,
        };
      });
  }

  private clearElements(nodes: RevealNode[]): void {
    for (const node of nodes) {
      node.element = undefined;
      node.painted = -1;
      this.clearElements(node.children);
    }
  }

  private paintChildren(
    parent: HTMLElement,
    nodes: RevealNode[],
    characters: number,
    fadeStart: number,
    fade: number,
    offset: number,
    complete: boolean
  ): boolean {
    let index = 0,
      faded = false;
    for (const node of nodes) {
      const remaining = characters - offset;
      if (remaining <= 0 && !(complete && !node.length)) break;
      // Match the ordinary reveal: an empty structural shell appears with its first text.
      if (
        !complete &&
        !node.length &&
        node.shell &&
        !['BR', 'HR', 'IMG', 'INPUT'].includes(node.shell.nodeName)
      )
        continue;
      node.element ??= node.shell
        ? (node.shell.cloneNode(false) as HTMLElement)
        : document.createElement('span');
      if (parent.childNodes[index] !== node.element)
        parent.insertBefore(node.element, parent.childNodes[index] ?? null);
      index++;
      const shown = Math.min(node.length, Math.ceil(remaining));
      const full = shown === node.length;
      if (
        !(
          full &&
          node.painted === node.length &&
          !node.faded &&
          offset + node.length <= fadeStart
        )
      ) {
        if (node.text !== undefined) {
          const normal = fade
            ? Math.max(0, Math.min(shown, fadeStart - offset))
            : shown;
          let prefix = node.element.firstChild;
          if (prefix?.nodeType !== Node.TEXT_NODE) {
            prefix = document.createTextNode('');
            node.element.replaceChildren(prefix);
          }
          const text = node.characters!.slice(0, normal).join('');
          if (prefix.textContent !== text) prefix.textContent = text;
          for (let position = normal; position < shown; position++) {
            const childIndex = position - normal + 1;
            let tail = node.element.childNodes[childIndex] as
              | HTMLElement
              | undefined;
            if (!tail) {
              tail = document.createElement('span');
              node.element.appendChild(tail);
            }
            const character = node.characters![position];
            if (tail.textContent !== character) tail.textContent = character;
            const progress = Math.min(
              1,
              Math.max(0, (characters - offset - position) / fade)
            );
            const className = `reveal-fragment reveal-soft-${Math.round(
              progress * 8
            )}`;
            if (tail.className !== className) tail.className = className;
          }
          while (node.element.childNodes.length > shown - normal + 1)
            node.element.lastChild!.remove();
          node.faded = normal < shown;
        } else {
          node.faded = this.paintChildren(
            node.element,
            node.children,
            characters,
            fadeStart,
            fade,
            offset,
            complete
          );
        }
        node.painted = shown;
      }
      faded ||= node.faded;
      offset += node.length;
    }
    while (parent.childNodes.length > index) parent.lastChild!.remove();
    return faded;
  }
}
